import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useAuthStore } from './stores/authStore'
import PromotionalBanner from './components/PromotionalBanner'
import CookieConsent from './components/CookieConsent'
import ScrollToTop from './components/ScrollToTop'
import SEO from './components/SEO'

// Layouts
import MainLayout from './layouts/MainLayout'
import AuthLayout from './layouts/AuthLayout'

// Pages
import Landing from './pages/Landing'
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'
import AuthCallback from './pages/auth/AuthCallback'
import ForgotPassword from './pages/auth/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Dashboard from './pages/Dashboard'
import ModuleList from './pages/modules/ModuleList'
import ModuleView from './pages/modules/ModuleView'
import LabList from './pages/labs/LabList'
import LabView from './pages/labs/LabView'
import CapstoneList from './pages/capstones/CapstoneList'
import CapstoneView from './pages/capstones/CapstoneView'
import CaseStudyView from './pages/case-studies/CaseStudyView'
import PremiumTemplates from './pages/PremiumTemplates'
import Resources from './pages/Resources'
import Badges from './pages/Badges'
import Leaderboard from './pages/Leaderboard'
import Discussions from './pages/Discussions'
import DiscussionThread from './pages/DiscussionThread'
import Notifications from './pages/Notifications'
import Analytics from './pages/Analytics'
import Certificate from './pages/Certificate'
import CertificateView from './pages/CertificateView'
import MyCertificates from './pages/MyCertificates'
import Bookmarks from './pages/Bookmarks'
import Profile from './pages/Profile'
import Admin from './pages/Admin'
import InstructorDashboard from './pages/InstructorDashboard'
import InstructorStudentDetail from './pages/InstructorStudentDetail'
import Checkout from './pages/Checkout'
import PaymentSuccess from './pages/PaymentSuccess'
import PaymentCancelled from './pages/PaymentCancelled'
import TermsOfService from './pages/TermsOfService'
import PrivacyPolicy from './pages/PrivacyPolicy'
import NotFound from './pages/NotFound'
import Debug from './pages/Debug'

// Protected Route Components
import TierProtectedRoute from './components/TierProtectedRoute'
function ProtectedRoute({ children }) {
  const { isAuthenticated, _hasHydrated } = useAuthStore()
  
  // Wait for Zustand to hydrate from localStorage
  if (!_hasHydrated) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="text-gray-600">Loading...</div>
    </div>
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  return children
}

function App() {
  return (
    <>
      <SEO />
      <Toaster
        position="top-center"
        reverseOrder={false}
        gutter={8}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
      <Router>
        <PromotionalBanner />
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<>
            <SEO
              title="CTE Course Platform - AI Prompting & Agent Systems"
              description="Master AI Prompting and Agent Systems with OpenAI Assistants. Build production-ready agents you can ship."
              url="https://www.mytechcourses.com/"
              image="https://www.mytechcourses.com/branding/cte-course-platform-icon.svg"
            />
            <Landing />
          </>} />
          <Route path="/terms" element={
            <>
              <SEO title="Terms of Service - CTE Course Platform" url="https://www.mytechcourses.com/terms" />
              <TermsOfService />
            </>
          } />
          <Route path="/privacy" element={
            <>
              <SEO title="Privacy Policy - CTE Course Platform" url="https://www.mytechcourses.com/privacy" />
              <PrivacyPolicy />
            </>
          } />
          <Route path="/debug" element={<Debug />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* OAuth Callback - outside layouts for clean redirect */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Payment Routes */}
        <Route path="/checkout" element={
          <>
            <SEO title="Checkout - CTE Course Platform" url="https://www.mytechcourses.com/checkout" />
            <Checkout />
          </>
        } />
        <Route path="/payment/success" element={
          <>
            <SEO title="Payment Success - CTE Course Platform" url="https://www.mytechcourses.com/payment/success" />
            <PaymentSuccess />
          </>
        } />
        <Route path="/payment/cancelled" element={
          <>
            <SEO title="Payment Cancelled - CTE Course Platform" url="https://www.mytechcourses.com/payment/cancelled" />
            <PaymentCancelled />
          </>
        } />

        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<>
            <SEO title="Login - CTE Course Platform" url="https://www.mytechcourses.com/login" />
            <Login />
          </>} />
          <Route path="/register" element={<>
            <SEO title="Register - CTE Course Platform" url="https://www.mytechcourses.com/register" />
            <Register />
          </>} />
        </Route>

        {/* Protected Course Routes */}
        <Route element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }>
          <Route path="/dashboard" element={<>
            <SEO title="Dashboard - CTE Course Platform" url="https://www.mytechcourses.com/dashboard" />
            <Dashboard />
          </>} />

          {/* Modules */}
          <Route path="/modules" element={<>
            <SEO title="Modules - CTE Course Platform" url="https://www.mytechcourses.com/modules" />
            <ModuleList />
          </>} />
          <Route path="/modules/:id" element={<ModuleView />} />
          
          {/* Labs */}
          <Route path="/labs" element={<>
            <SEO title="Labs - CTE Course Platform" url="https://www.mytechcourses.com/labs" />
            <LabList />
          </>} />
          <Route path="/labs/:id" element={<LabView />} />
          
          {/* Capstones */}
          <Route path="/capstones" element={<>
            <SEO title="Capstones - CTE Course Platform" url="https://www.mytechcourses.com/capstones" />
            <CapstoneList />
          </>} />
          <Route path="/capstones/:id" element={<CapstoneView />} />
          
          {/* Case Studies */}
          <Route path="/case-studies/:id" element={<CaseStudyView />} />
          
          {/* Premium Templates - Protected */}
          <Route 
            path="/premium-templates" 
            element={
              <TierProtectedRoute feature="premium_resources">
                <>
                  <SEO title="Premium Templates - CTE Course Platform" url="https://www.mytechcourses.com/premium-templates" />
                  <PremiumTemplates />
                </>
              </TierProtectedRoute>
            } 
          />
          
          {/* Resources & Badges */}
          <Route path="/resources" element={<>
            <SEO title="Resources - CTE Course Platform" url="https://www.mytechcourses.com/resources" />
            <Resources />
          </>} />
          <Route path="/badges" element={<>
            <SEO title="Badges - CTE Course Platform" url="https://www.mytechcourses.com/badges" />
            <Badges />
          </>} />
          <Route path="/leaderboard" element={<>
            <SEO title="Leaderboard - CTE Course Platform" url="https://www.mytechcourses.com/leaderboard" />
            <Leaderboard />
          </>} />

          {/* Discussions */}
          <Route path="/discussions" element={<>
            <SEO title="Discussions - CTE Course Platform" url="https://www.mytechcourses.com/discussions" />
            <Discussions />
          </>} />
          <Route path="/discussions/:id" element={<DiscussionThread />} />
          
          {/* Notifications */}
          <Route path="/notifications" element={<>
            <SEO title="Notifications - CTE Course Platform" url="https://www.mytechcourses.com/notifications" />
            <Notifications />
          </>} />

          {/* Analytics */}
          <Route path="/analytics" element={<>
            <SEO title="Analytics - CTE Course Platform" url="https://www.mytechcourses.com/analytics" />
            <Analytics />
          </>} />

          {/* Profile & Certificates */}
          <Route path="/profile" element={<>
            <SEO title="Profile - CTE Course Platform" url="https://www.mytechcourses.com/profile" />
            <Profile />
          </>} />
          <Route
            path="/certificate" 
            element={
              <TierProtectedRoute feature="certificates">
                <>
                  <SEO title="Certificate - CTE Course Platform" url="https://www.mytechcourses.com/certificate" />
                  <Certificate />
                </>
              </TierProtectedRoute>
            } 
          />
          <Route 
            path="/certificates/:id" 
            element={
              <TierProtectedRoute feature="certificates">
                <CertificateView />
              </TierProtectedRoute>
            } 
          />
          <Route 
            path="/my-certificates" 
            element={
              <TierProtectedRoute feature="certificates">
                <>
                  <SEO title="My Certificates - CTE Course Platform" url="https://www.mytechcourses.com/my-certificates" />
                  <MyCertificates />
                </>
              </TierProtectedRoute>
            } 
          />
          
          {/* Bookmarks */}
          <Route path="/bookmarks" element={<>
            <SEO title="Bookmarks - CTE Course Platform" url="https://www.mytechcourses.com/bookmarks" />
            <Bookmarks />
          </>} />

          {/* Admin & Instructor */}
          <Route path="/admin" element={<>
            <SEO title="Admin - CTE Course Platform" url="https://www.mytechcourses.com/admin" />
            <Admin />
          </>} />
          <Route path="/instructor" element={<>
            <SEO title="Instructor Dashboard - CTE Course Platform" url="https://www.mytechcourses.com/instructor" />
            <InstructorDashboard />
          </>} />
          <Route path="/instructor/student/:assignmentId" element={<InstructorStudentDetail />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<>
          <SEO title="Page Not Found - CTE Course Platform" />
          <NotFound />
        </>} />
      </Routes>
      <CookieConsent />
    </Router>
    </>
  )
}

export default App
